import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";

const PlayerContext = createContext(null);

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  if (!audioRef.current && typeof window !== "undefined") {
    audioRef.current = new Audio();
  }
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRate] = useState(1);
  const sleepTimerRef = useRef(null);
  const [sleepMinutes, setSleepMinutes] = useState(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => setProgress(audio.currentTime);
    const onLoaded = () => setDuration(audio.duration || 0);
    const onEnd = () => setPlaying(false);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("ended", onEnd);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("ended", onEnd);
    };
  }, []);

  const play = useCallback((track) => {
    const audio = audioRef.current;
    if (!current || current.audioUrl !== track.audioUrl) {
      audio.src = track.audioUrl;
      audio.playbackRate = rate;
      if (track.startAt) audio.currentTime = track.startAt;
      setCurrent(track);
    }
    audio.play().catch(() => {});
    setPlaying(true);
  }, [current, rate]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    setPlaying(false);
  }, []);

  const toggle = useCallback(() => {
    if (!current) return;
    if (playing) pause();
    else audioRef.current.play().then(() => setPlaying(true)).catch(() => {});
  }, [playing, current, pause]);

  const seek = useCallback((t) => {
    if (audioRef.current) audioRef.current.currentTime = t;
  }, []);

  const setPlaybackRate = useCallback((r) => {
    if (audioRef.current) audioRef.current.playbackRate = r;
    setRate(r);
  }, []);

  const setSleepTimer = useCallback((minutes) => {
    if (sleepTimerRef.current) clearTimeout(sleepTimerRef.current);
    setSleepMinutes(minutes);
    if (minutes) {
      sleepTimerRef.current = setTimeout(() => {
        pause();
        setSleepMinutes(null);
      }, minutes * 60 * 1000);
    }
  }, [pause]);

  return (
    <PlayerContext.Provider value={{ current, playing, progress, duration, rate, sleepMinutes, play, pause, toggle, seek, setPlaybackRate, setSleepTimer }}>
      {children}
    </PlayerContext.Provider>
  );
}

export const usePlayer = () => useContext(PlayerContext);
